import { am } from '../utils';

import httpClient from '@/utils/httpClient';

const GET_COLLECTION = am(`GET_LIST`);
const GET_DETAILS = am(`GET_LIST_ITEM`);

export const MILESTONE_UPDATE = am(`MILESTONE_UPDATE`);

const isActive = m => {
  if (m.deleted) {
    return false;
  }

  if (m.finished) {
    return false;
  }

  return true;
};

const isFinished = m => {
  return m.finished ? true : false;
};

const sortMilestones = (a, b) => {
  if (!a?.id && !b?.id) {
    return 0;
  }

  if (!b?.id) {
    return 1;
  }

  if (!a?.id) {
    return -1;
  }

  if (a.finished && b.finished) {
    return 0;
  }

  if (!a.finished) {
    return -1;
  }

  if (!b.finished) {
    return 1;
  }

  return 0;
};

const sortByStatus = (a, b) => {
  if (isActive(a) && isActive(a) === isActive(b)) {
    return new Date(a.dueAt).getTime() - new Date(b.dueAt).getTime();
  }

  if (isActive(a)) {
    return -1;
  }

  if (isActive(b)) {
    return 1;
  }

  if (isFinished(a) === isFinished(b)) {
    return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
  }

  if (isFinished(a)) {
    return -1;
  }

  if (isFinished(b)) {
    return 1;
  }

  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
};

const state = {
  total: 0,
  from: 0,
  data: [],
  isRequestPending: false,
  isRequestFailed: true,

  details: {}
};
const getters = {
  milestonesByDetailsId: state => id => {
    return state.details[id].milestones.sort(sortByStatus);
  }
};

const mutations = {
  [GET_COLLECTION.STARTED](state, { initialize, refresh }) {
    if (!refresh && initialize) {
      state.isRequestPending = true;
    }
    state.isRequestFailed = false;
    if (initialize) {
      state.data = [];
    }
  },
  [GET_COLLECTION.COMPLETED](state, { data, total, refresh }) {
    state.isRequestPending = false;
    if (refresh) {
      state.data = data;
    } else {
      for (const item of data) {
        const index = state.data.findIndex(({ id }) => item.id === id);
        if (index > -1) {
          state.data[index] = item;
        } else {
          state.data.push(item);
        }
      }
    }

    state.total = total;
  },
  [GET_COLLECTION.FAILED](state) {
    state.isRequestPending = false;
    state.isRequestFailed = true;
  },
  [GET_DETAILS.STARTED](state, { id }) {
    state.details[id] = {
      isRequestPending: true,
      isRequestFailed: false,
      milestones: []
    };
  },
  [GET_DETAILS.COMPLETED](state, { id, milestones }) {
    if (!state.details[id]) {
      state.details[id] = {};
    }

    state.details[id].isRequestPending = false;
    state.details[id].isRequestFailed = false;
    state.details[id].milestones = milestones;
  },
  [GET_DETAILS.FAILED](state, { id }) {
    if (!state.details[id]) {
      state.details[id] = {};
    }

    state.details[id].isRequestPending = false;
    state.details[id].isRequestFailed = true;
  },
  ['GET_DETAILS.RESET'](state) {
    state.details = [];
  },
  [MILESTONE_UPDATE.STARTED](state, { inventionId, milestoneId }) {
    const milestone = state.details[inventionId].milestones.find(m => m.id === milestoneId);
    milestone.isUpdating = true;
  },
  [MILESTONE_UPDATE.COMPLETED](state, { inventionId, milestoneId }) {
    const milestone = state.details[inventionId].milestones.find(m => m.id === milestoneId);
    milestone.isUpdating = false;
  },
  taskCreated(state, item) {
    const task = item.metadata.task;
    if (!task) {
      return;
    }

    const invention = state.details[item.inventionId];

    if (!invention) {
      return;
    }

    let milestones = invention?.milestones;

    if (!milestones) {
      milestones = [];
      invention.milestones = milestones;
    }

    let milestone = milestones.find(m => m.id === task.workflow?.milestoneId ?? null);

    if (!milestone) {
      if (!task.workflow?.milestoneId) {
        milestone = {
          id: null,
          title: 'No Milestone',
          expanded: true,
          tasks: []
        };
        milestones.unshift(milestone);
      } else {
        return;
      }
    }
    const taskIndex = milestone.tasks.sort((a, b) => a.order - b.order).findIndex(t => t.id === task.id);
    if (taskIndex > -1) {
      return;
    }

    milestone.tasks.push(task);
  },
  taskFetched(state, { inventionId, task }) {
    const milestones = state.details[inventionId]?.milestones;

    if (!milestones?.length) {
      return;
    }

    const milestone = milestones.find(m => m.id === task.workflow.milestoneId);

    if (!milestone) {
      return;
    }

    if (!milestone.tasks) {
      milestone.tasks = [];
    }

    const taskIndex = milestone.tasks.findIndex(t => t.id === task.id);
    if (taskIndex > -1) {
      milestone.tasks.splice(taskIndex, 1, task);
    }
    milestone.tasks.sort((a, b) => a.order - b.order);
  },
  taskUpdated(state, item) {
    const milestones = state.details[item.invention.id]?.milestones;

    if (!milestones?.length) {
      return;
    }

    const milestone = milestones.find(m => m.id === item.workflow.milestoneId);
    const taskIndex = milestone.tasks.findIndex(t => t.id === item.id);
    if (taskIndex > -1) {
      milestone.tasks[taskIndex] = item;
    } else {
      const previousMilestone = milestones.find(m => m.tasks?.some(t => t.id === item.id));
      const index = previousMilestone?.tasks?.findIndex(t => t.id === item.id);
      if (index > -1) {
        previousMilestone.tasks.splice(index, 1);
      }
      milestone.tasks.push(item);
    }
  },

  milestoneUpdated(state, item) {
    const milestones = state.details[item.inventionId]?.milestones;
    if (!milestones?.length) {
      return;
    }

    const milestone = milestones.find(m => m.id === item.metadata.id);
    const { title, dueAt, assignees, clientDueDate, diffs } = item.metadata;
    const deletedUpdate = diffs.find(d => d.originalProperty === 'deleted');
    if (deletedUpdate) {
      milestone.deleted = deletedUpdate.newValue.toLowerCase() === 'true';
    }

    const finishedUpdate = diffs.find(d => d.originalProperty === 'finished');
    if (finishedUpdate) {
      milestone.finished = finishedUpdate.newValue.toLowerCase() === 'true';
    }

    milestone.completed = milestone.finished || milestone.deleted;
    milestone.expanded = !milestone.completed;
    milestone.title = title;
    milestone.dueAt = dueAt;
    milestone.clientDueDate = clientDueDate;
    milestone.assignees = assignees;
  },
  milestoneCreated(state, item) {
    const invention = state.details[item.inventionId];

    if (!invention) {
      return;
    }

    if (!invention.milestones) {
      invention.milestones = [];
    }

    const milestones = invention.milestones;

    const milestone = milestones.find(m => m.id === item.metadata.id);
    if (milestone) {
      return;
    }
    const newItem = { ...item.metadata, tasks: [], expanded: true, completed: false, isUpdating: false, deleted: false, finished: false };
    milestones.splice(1, 0, newItem);
  },
  milestoneFetched(state, milestone) {
    const milestones = state.details[milestone.inventionId]?.milestones;

    if (!milestones?.length) {
      return;
    }

    const target = milestones.find(m => m.id === milestone.id);
    if (target) {
      target.isUpdating = false;
      target.workflow = milestone.workflow;
      target.nextTask = milestone.nextTask;
    }
  },
  milestoneFinished(state, { inventionId, milestoneId }) {
    const milestones = state.details[inventionId]?.milestones;

    if (!milestones?.length) {
      return;
    }

    const milestone = milestones.find(m => m.id === milestoneId);
    milestone.finished = true;
    milestone.completed = true;
    milestone.expanded = false;

    state.details[inventionId].milestones = state.details[inventionId].milestones.sort(sortMilestones);
  },
  milestoneCancelled(state, { inventionId, milestoneId }) {
    const milestones = state.details[inventionId]?.milestones;

    if (!milestones?.length) {
      return;
    }

    const milestone = milestones.find(m => m.id === milestoneId);
    milestone.deleted = true;
    milestone.completed = true;
    milestone.expanded = false;

    state.details[inventionId].milestones = state.details[inventionId].milestones.sort(sortMilestones);
  }
};
const actions = {
  async getCollection({ commit }, { qs, skip, sort, size, refresh } = {}) {
    try {
      commit(GET_COLLECTION.STARTED, {
        initialize: typeof skip === 'undefined' && !refresh,
        refresh
      });

      const params = new URLSearchParams();
      if (skip) {
        params.set('skip', skip);
      }

      if (qs) {
        params.set('qs', qs);
      }

      if (sort) {
        params.set('sort', sort);
      }
      if (size) {
        params.set('size', size);
      } else {
        params.set('size', 40);
      }

      const response = await httpClient.get(`/api/search/overview/milestones/obsolete?` + params.toString());

      commit(GET_COLLECTION.COMPLETED, {
        data: response.data.map(item => ({
          id: item.inventionId,
          references: item.references,
          title: item.name,
          nextTaskTitle: item.nextTaskTitle,
          status: item.status,
          roadmap: item.roadmap,
          dueAt: item.dueAt,
          clientDueDate: item.workflow.milestoneClientDueDate,
          milestoneDueAt: item.workflow.milestoneDueAt,
          nextTask: item.nextTask,
          nextCriticalTask: item.nextCriticalTask
        })),
        total: response.total,
        refresh
      });
    } catch (e) {
      commit(GET_COLLECTION.FAILED);
      throw e;
    }
  },
  async getDetails({ commit }, { id }) {
    try {
      commit(GET_DETAILS.STARTED, {
        id
      });

      const response = await httpClient.get(`/api/search/overview/inventions/${id}/milestones`);

      commit(GET_DETAILS.COMPLETED, {
        id,
        milestones: response
          .map(milestone => ({
            id: milestone.id,
            title: milestone.title,
            dueAt: milestone.dueAt,
            assignees: milestone.assignees,
            clientDueDate: milestone.clientDueDate,
            completed: milestone.finished,
            deleted: milestone.deleted,
            finished: milestone.finished || milestone.delete,
            finishedAt: milestone.finishedAt,
            deletedAt: milestone.deletedAt,
            createdAt: milestone.createdAt,
            expanded: !(milestone.finished || milestone.deleted),
            tasks: Array.isArray(milestone.tasks) ? milestone.tasks : [],
            workflow: milestone.workflow
          }))
          .sort(sortMilestones)
      });
    } catch (e) {
      commit(GET_DETAILS.FAILED, { id });
      throw e;
    }
  },
  resetDetails({ commit }) {
    commit('GET_DETAILS.RESET');
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
